import { TWITTER_AUTOMATION_STAGE_STATUSES } from './twitterAutomationStageStatuses'
import { profileSettingsUrl } from './twitterUrls'
import { XSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { TwitterRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import { evaluateXpath, getNearestElementMatch } from '../interruptibleXpath'
import { setInputTextElement } from '../reactInputElementUtils'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement, shouldUpdateSetting } from '../utils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === '')

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// "Edit Profile - Location" automation utils

/**
 * Find the modal element by identifying the tile, and then getting the
 * nearest ancestor that also is the ancestor of an input element.
 */
async function getEditProfileModal(
  contextNode: Node,
): Promise<HTMLElement | null> {
  const editProfileLabel = await getElement<HTMLElement>(
    contextNode,
    'descendant::*[text() = "Edit profile"]',
  )

  if (!editProfileLabel) {
    return null
  }

  const ancestorsContainingInputs = evaluateXpath<HTMLElement>(
    editProfileLabel,
    'ancestor-or-self::*[count(./descendant::input) > 0]',
    { requireDocumentOrder: true },
  )

  return ancestorsContainingInputs[ancestorsContainingInputs.length - 1]
}

function getLocationInputElement(): HTMLInputElement {
  const locationLabel = evaluateXpath<HTMLElement>(
    document,
    'descendant::*[text() = "Location"]',
  )[0]

  const ret = getNearestElementMatch<HTMLInputElement>(
    locationLabel,
    'input',
    'following',
  )
  return ret
}

async function getSaveButton(): Promise<HTMLElement> {
  const editProfileLabel = evaluateXpath<HTMLElement>(
    document,
    `descendant::*[text() = "Edit profile"]`,
  )[0]
  const saveButton = getNearestElementMatch(
    editProfileLabel,
    '*[@role = "button" and count(./descendant::*[text() = "Save"]) > 0]',
    'following',
  )
  return saveButton
}

async function gatherLocation(): Promise<string> {
  await getEditProfileModal(document)

  const locationInputElement = getLocationInputElement()
  return locationInputElement.value
}

async function setLocation(value: string): Promise<void> {
  await getEditProfileModal(document)

  const locationInputElement = getLocationInputElement()
  setInputTextElement(locationInputElement, value)

  const saveButton = await getSaveButton()

  if (!saveButton) {
    throw new Error(
      'Could not find element for save button for Twitter location profile',
    )
  }
  saveButton.click()
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_PROFILE_LOCATION = 'GATHER_SETTING_PROFILE_LOCATION',
  UPDATE_SETTING_PROFILE_LOCATION = 'UPDATE_SETTING_PROFILE_LOCATION',
  UNDO_SETTING_PROFILE_LOCATION = 'UNDO_SETTING_PROFILE_LOCATION',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_PROFILE_LOCATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: profileSettingsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const location = await gatherLocation()
      await updateStoredInitialValues(
        { [XSettingKey.PROFILE_REMOVE_LOCATION]: location },
        TwitterRecommendationKey.TWITTER_LOCATION_PROFILE,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_PROFILE_LOCATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: profileSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      const shouldUpdate = shouldUpdateSetting(
        settingsSelected,
        initialValues,
        XSettingKey.PROFILE_REMOVE_LOCATION,
        getIsFulfilledFn,
      )

      // Skip if not selected
      if (!shouldUpdate) {
        return null
      }

      // Clear location if not dry
      if (!isDry) {
        return setLocation('')
      }
      return null
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_PROFILE_LOCATION,
    status: TWITTER_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: profileSettingsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      const shouldUndo = shouldUpdateSetting(
        settingsSelected,
        initialValues,
        XSettingKey.PROFILE_REMOVE_LOCATION,
        getIsFulfilledFn,
      )

      // Skip if not selected
      if (!shouldUndo) {
        return null
      }

      // Clear location if not dry
      if (isDry) {
        return null
      }

      return setLocation(initialValues[XSettingKey.PROFILE_REMOVE_LOCATION])
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
